/** Paramètres → Taxes : régime société + taux par défaut des produits. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { useCompany } from "../../company/CompanyProvider";
import { isCompanyOwner } from "./owner";

export type VatRegime = "standard" | "franchise";

export type TaxRate = {
  id: number;
  label: string;
  rate_bp: number;
  is_default: boolean;
};

type TaxesPayload = {
  vat_regime: VatRegime;
  default_tax_rate_id: number | null;
  mention: string | null;
  tax_rates: TaxRate[];
};

function ratePct(bp: number): string {
  const n = bp / 100;
  return Number.isInteger(n) ? `${n} %` : `${n.toLocaleString("fr-FR")} %`;
}

export function TaxesTab() {
  const { company, reload } = useCompany();
  const [regime, setRegime] = useState<VatRegime>("standard");
  const [defaultId, setDefaultId] = useState<number | null>(null);
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [mention, setMention] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setMsg("");
    setErr("");
    facturxFetch<TaxesPayload>(`tax-rates.php?company_id=${company.id}`)
      .then((data) => {
        setRegime(data.vat_regime === "franchise" ? "franchise" : "standard");
        setDefaultId(data.default_tax_rate_id);
        setMention(data.mention);
        setRates(
          (data.tax_rates || []).map((r) => ({
            ...r,
            id: Number(r.id),
            rate_bp: Number(r.rate_bp),
          }))
        );
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Taux indisponibles"));
  }, [company]);

  if (!company) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);
  const locked = regime === "franchise";

  async function save() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const data = await facturxFetch<TaxesPayload>("tax-rates.php", {
        method: "PATCH",
        body: JSON.stringify({
          company_id: company.id,
          vat_regime: regime,
          default_tax_rate_id: defaultId,
        }),
      });
      setRegime(data.vat_regime === "franchise" ? "franchise" : "standard");
      setDefaultId(data.default_tax_rate_id);
      setMention(data.mention);
      setRates((data.tax_rates || []).map((r) => ({ ...r, id: Number(r.id), rate_bp: Number(r.rate_bp) })));
      await reload();
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Taxes</h2>
        <p>Régime de la société, puis le taux par défaut des produits.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre les taxes.</p>
        ) : null}
      </header>

      <div className="fx-fmt-row">
        <span>Régime</span>
        <div className="fx-fmt-picks" role="radiogroup" aria-label="Régime de TVA">
          <button
            type="button"
            role="radio"
            aria-checked={regime === "standard"}
            className={regime === "standard" ? "is-on" : ""}
            disabled={!owner}
            onClick={() => {
              setRegime("standard");
              const twenty = rates.find((r) => r.rate_bp === 2000);
              if (twenty) setDefaultId(twenty.id);
            }}
          >
            <strong>Assujetti à la TVA</strong>
            <em>Les taux s’appliquent aux produits et aux pièces.</em>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={regime === "franchise"}
            className={regime === "franchise" ? "is-on" : ""}
            disabled={!owner}
            onClick={() => {
              setRegime("franchise");
              const zero = rates.find((r) => r.rate_bp === 0);
              if (zero) setDefaultId(zero.id);
            }}
          >
            <strong>Micro — franchise en base</strong>
            <em>0 % · mention art. 293 B du CGI.</em>
          </button>
        </div>
      </div>

      {locked && mention ? (
        <p className="fx-co-form__hint">{mention} Cette mention figurera sur les pièces.</p>
      ) : (
        <p className="fx-co-form__hint">
          Les nouveaux produits prennent le taux coché. Un produit peut en avoir un autre (ex. 5,5 %).
        </p>
      )}

      <div className="fx-tax-list" role="radiogroup" aria-label="Taux par défaut">
        {rates.map((rate) => {
          const on = defaultId === rate.id;
          return (
            <label key={rate.id} className={on ? "is-on" : ""}>
              <input
                type="radio"
                name="fx-tax-default"
                checked={on}
                disabled={!owner || locked}
                onChange={() => setDefaultId(rate.id)}
              />
              <strong>{rate.label}</strong>
              <em>{ratePct(rate.rate_bp)}</em>
            </label>
          );
        })}
      </div>

      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
